import { useParams, Link } from "react-router-dom";
import {
  doc, getDoc, collection, query, where, getDocs, orderBy, onSnapshot
} from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import AdminHeader from "../components/AdminHeader";
import AdminSidebar from "../components/AdminSidebar";

function NeonCard({ children }) {
  return (
    <div className="rounded-2xl border border-[#D4FF00]/30 bg-black/60 p-4 shadow-[0_0_40px_rgba(212,255,0,0.1)]">
      {children}
    </div>
  );
}

/* ----------------- Robust date/time parsing helpers ----------------- */

// Parse "HH:mm", "H:mm", "11", "11am", "11:30 PM", "23:05", returns {H, M}
function parseClock(timeStr) {
  if (!timeStr || typeof timeStr !== "string") return { H: 0, M: 0 };
  const s = timeStr.trim().toLowerCase();

  // AM/PM?
  const ampmMatch = s.match(/^\s*(\d{1,2})(?::(\d{1,2}))?\s*(am|pm)\s*$/i);
  if (ampmMatch) {
    let h = parseInt(ampmMatch[1], 10);
    const m = parseInt(ampmMatch[2] ?? "0", 10);
    const ampm = ampmMatch[3].toLowerCase();
    if (ampm === "pm" && h < 12) h += 12;
    if (ampm === "am" && h === 12) h = 0;
    return { H: clamp(h, 0, 23), M: clamp(m, 0, 59) };
  }

  // 24h "HH:mm" or "H:mm" or "HH"
  const hm = s.match(/^(\d{1,2})(?::(\d{1,2}))?$/);
  if (hm) {
    const h = clamp(parseInt(hm[1], 10), 0, 23);
    const m = clamp(parseInt(hm[2] ?? "0", 10), 0, 59);
    return { H: h, M: m };
  }

  // Fallback
  return { H: 0, M: 0 };
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, Number.isFinite(n) ? n : min));
}

// Parse "YYYY-MM-DD" | "YYYY/M/D" | "YYYY-MM-DD HH:mm" | ISO string | Date | number
function coerceToDate(value) {
  if (!value && value !== 0) return null;

  // Firestore Timestamp
  if (value && typeof value.toDate === "function") {
    try { return value.toDate(); } catch { /* ignore */ }
  }
  // Already Date
  if (value instanceof Date && !isNaN(+value)) return value;

  // Number (ms or s)
  if (typeof value === "number") {
    // if looks like seconds, convert
    const ms = value < 1e12 ? value * 1000 : value;
    const d = new Date(ms);
    return isNaN(+d) ? null : d;
  }

  if (typeof value === "string") {
    const s = value.trim();

    // "YYYY-MM-DD HH:mm" or "YYYY/MM/DD HH:mm"
    let m = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})[ T](\d{1,2}):(\d{1,2})$/);
    if (m) {
      const y = parseInt(m[1], 10);
      const mo = parseInt(m[2], 10) - 1;
      const d = parseInt(m[3], 10);
      const H = parseInt(m[4], 10);
      const M = parseInt(m[5], 10);
      return new Date(y, mo, d, H, M, 0, 0); // local time
    }

    // "YYYY-MM-DD" or "YYYY/M/D" → local midnight
    m = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
    if (m) {
      const y = parseInt(m[1], 10);
      const mo = parseInt(m[2], 10) - 1;
      const d = parseInt(m[3], 10);
      return new Date(y, mo, d, 0, 0, 0, 0);
    }

    // Try native (ISO, etc.)
    const d2 = new Date(s);
    if (!isNaN(+d2)) return d2;
  }

  return null;
}

// Build a Date from date string + time string in LOCAL time safely
function fromDateAndTimeStrings(dateStr, timeStr) {
  if (!dateStr) return null;
  const dm = String(dateStr).trim().match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  const { H, M } = parseClock(timeStr || "00:00");
  if (dm) {
    const y = parseInt(dm[1], 10);
    const mo = parseInt(dm[2], 10) - 1;
    const d = parseInt(dm[3], 10);
    return new Date(y, mo, d, H, M, 0, 0);
  }
  // If dateStr wasn't a simple Y-M-D, fall back to coerce + patch the time
  const base = coerceToDate(dateStr);
  if (!base) return null;
  base.setHours(H, M, 0, 0);
  return base;
}

// Normalize any booking shape to {id, status, start:Date, end:Date, seatId, userId}
function normalizeBooking(id, data) {
  const status = (data.status || "").toLowerCase();

  // Prefer explicit start/end (various shapes)
  let start = coerceToDate(data.start);
  let end = coerceToDate(data.end);

  // If missing, try combined strings
  if (!start && typeof data.startStr === "string") start = coerceToDate(data.startStr);
  if (!end && typeof data.endStr === "string") end = coerceToDate(data.endStr);

  // If still missing, try separate date + times
  if (!start && data.date) start = fromDateAndTimeStrings(data.date, data.startTime);
  if (!end && data.date) {
    end = fromDateAndTimeStrings(data.date, data.endTime);
  }

  // If only duration present, derive end
  if (start && !end && Number.isFinite(data.durationMinutes)) {
    end = new Date(start.getTime() + data.durationMinutes * 60000);
  }

  // Last resort: copy start to end
  if (!end && start) end = new Date(start);

  return {
    id,
    status,
    start: start || null,
    end: end || start || null,
    seatId: data.seatId || data.pcId || data.seat || "—",
    userId: data.userId || data.uid || null,
  };
}
/* ------------------------------------------------------------------- */

export default function AdminUserDetail() {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const [creditLogs, setCreditLogs] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(true);

  // Load user
  useEffect(() => {
    (async () => {
      const docRef = doc(db, "users", userId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) setUser({ id: docSnap.id, ...docSnap.data() });
      setLoading(false);
    })();
  }, [userId]);

  // Load this user's bookings from top-level /bookings
  useEffect(() => {
    (async () => {
      setLoadingBookings(true);
      const qRef = query(collection(db, "bookings"), where("userId", "==", userId));
      const snap = await getDocs(qRef);
      const rows = snap.docs.map((d) => normalizeBooking(d.id, d.data() || {}));
      // Sort by start desc for history
      rows.sort((a, b) => (b.start?.getTime?.() || 0) - (a.start?.getTime?.() || 0));
      setBookings(rows);
      setLoadingBookings(false);
    })();
  }, [userId]);

  // Live credit logs
  useEffect(() => {
    const qRef = query(collection(db, "users", userId, "creditLogs"), orderBy("timestamp", "desc"));
    const unsub = onSnapshot(qRef, (snap) => {
      const logs = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setCreditLogs(logs);
    });
    return () => unsub();
  }, [userId]);

  // Hooks must be before any early return
  const nextBooking = useMemo(() => {
    const now = Date.now();
    const future = bookings
      .filter((b) => b.start && b.status !== "canceled" && b.start.getTime() > now)
      .sort((a, b) => a.start - b.start);
    return future[0] || null;
  }, [bookings]);

  if (loading || !user)
    return <div className="p-6 text-gray-300">Loading user details…</div>;

  return (
    <>
      <AdminHeader />
    <div className="flex flex-col md:flex-row">
      {/* Sidebar */}
      <div className="w-full md:w-48 bg-black border-b md:border-b-0 p-4">
        <AdminSidebar />
      </div>

      {/* Main Content */}
      <div className="flex-1 p-4 mt-4">
        <div className="max-w-4xl mx-auto text-white space-y-6">
          <h1 className="text-2xl font-bold text-[#D4FF00] mb-4">
            User Detail: {user.displayName || "Unnamed User"}
          </h1>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <NeonCard>
              <div className="text-[#D4FF00] font-semibold mb-2">User Info</div>
              <div className="text-sm text-gray-200 space-y-1">
                <div><strong>Name:</strong> {user.displayName || "—"}</div>
                <div><strong>Email:</strong> {user.email || "—"}</div>
                <div><strong>Credits:</strong> ${((user.credits || 0) / 100).toFixed(2)}</div>
                <div><strong>UID:</strong> {user.id}</div>
                <div><strong>Created:</strong> {user.createdAt?.toDate?.().toLocaleString?.() || "—"}</div>
              </div>
            </NeonCard>

            <NeonCard>
              <div className="text-[#D4FF00] font-semibold mb-2">Quick Links</div>
              <div className="text-sm flex flex-col gap-2">
                <Link to="/admin/users" className="text-sm hover:text-[#D4FF00] underline text-white">
                  ← Back to User Manager
                </Link>
                <Link to={`/admin/user-activity/${userId}`} className="text-sm hover:text-[#D4FF00] underline text-white">
                  View Activity Logs
                </Link>
              </div>
            </NeonCard>
          </div>

          {/* Upcoming booking */}
          <div>
            <h2 className="text-xl font-bold text-[#D4FF00] mb-3">Upcoming Booking</h2>
            <NeonCard>
              {loadingBookings ? (
                <div className="text-gray-400 text-sm">Loading…</div>
              ) : nextBooking ? (
                <div className="text-sm text-gray-200">
                  <div>
                    Seat: <span className="font-semibold text-white">{nextBooking.seatId}</span>
                  </div>
                  <div>
                    {nextBooking.start?.toLocaleString?.()} — {nextBooking.end?.toLocaleString?.()}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">Status: {nextBooking.status || "—"}</div>
                  <div className="mt-2">
                    <Link
                      to={{ pathname: "/admin/bookings", search: `?user=${userId}&booking=${nextBooking.id}` }}
                      state={{ user, booking: nextBooking }}
                      className="underline text-[#D4FF00] hover:text-white text-xs"
                    >
                      Open in Bookings
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="text-gray-400 text-sm">None.</div>
              )}
            </NeonCard>
          </div>

          {/* Booking history */}
          <div>
            <h2 className="text-xl font-bold text-[#D4FF00] mb-3">All Bookings</h2>
            <NeonCard>
              {loadingBookings ? (
                <div className="text-gray-400 text-sm">Loading…</div>
              ) : bookings.length === 0 ? (
                <div className="text-gray-400 text-sm">No bookings found.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-gray-300 border-b border-white/10 text-left">
                        <th className="py-2 pr-4">Start</th>
                        <th className="py-2 pr-4">End</th>
                        <th className="py-2 pr-4">Seat</th>
                        <th className="py-2 pr-4">Status</th>
                        <th className="py-2">Link</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-200">
                      {bookings.map((b) => (
                        <tr key={b.id} className="border-b border-white/5">
                          <td className="py-2 pr-4">{b.start?.toLocaleString?.() || "—"}</td>
                          <td className="py-2 pr-4">{b.end?.toLocaleString?.() || "—"}</td>
                          <td className="py-2 pr-4">{b.seatId}</td>
                          <td className="py-2 pr-4 capitalize">{b.status || "—"}</td>
                          <td className="py-2">
                            <Link
                              to={{ pathname: "/admin/bookings", search: `?user=${userId}&booking=${b.id}` }}
                              state={{ user, booking: b }}
                              className="underline text-[#D4FF00] hover:text-white text-xs"
                            >
                              Open
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </NeonCard>
          </div>

          {/* Credit History */}
          <div>
            <h2 className="text-xl font-bold text-[#D4FF00] mb-3">Credit History</h2>
            <NeonCard>
              {creditLogs.length === 0 ? (
                <div className="text-gray-400 text-sm">No credit logs found.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-gray-300 border-b border-white/10 text-left">
                        <th className="py-2 pr-4">Date</th>
                        <th className="py-2 pr-4">Change</th>
                        <th className="py-2 pr-4">Old Credits</th>
                        <th className="py-2 pr-4">New Credits</th>
                        <th className="py-2 pr-4">Admin</th>
                        <th className="py-2">Reason</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-200">
                      {creditLogs.map((log) => (
                        <tr key={log.id} className="border-b border-white/5">
                          <td className="py-2 pr-4">{log.timestamp?.toDate?.().toLocaleString?.() || "—"}</td>
                          <td className={`py-2 pr-4 ${log.amount > 0 ? "text-green-400" : "text-red-400"}`}>
                            {log.amount >= 0 ? "+" : ""}${(log.amount / 100).toFixed(2)}
                          </td>
                          <td className="py-2 pr-4">${(log.oldCredits / 100).toFixed(2)}</td>
                          <td className="py-2 pr-4">${(log.newCredits / 100).toFixed(2)}</td>
                          <td className="py-2 pr-4">{log.admin || "—"}</td>
                          <td className="py-2">{log.reason || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </NeonCard>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
